import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Standards article (mapped to the "Technical" goal token):
 * "ISO 23247 and the digital-twin standards: what they mean for you".
 * Plain-language tour of ISO/IEC 30173 vocabulary + the ISO 23247
 * manufacturing framework (OME / Data-Collection / Core / User entities,
 * digital thread, composition). Grounded in docs/standards/iso-23247-mapping.md.
 * Vendor-neutral; product only in the closing block.
 */
export const ISO_23247_STANDARDS_ARTICLE: Article = {
  slug: "iso-23247-digital-twin-standards",
  title: "ISO 23247 and the digital-twin standards: what they mean for you",
  deck:
    "Two ISO standards now define what a manufacturing digital twin is and how its parts fit together. You do not need to read them — but knowing what they say turns a vague buying decision into a checklist, and protects you from tools that use the words without the substance.",
  goal: "Technical",
  readingMins: 9,
  heroStat: { value: "4", label: "reference-architecture entities every real twin has" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "For years 'digital twin' meant whatever the person selling it wanted it to mean. That has quietly changed. There is now an international vocabulary for the term, ISO/IEC 30173, and a dedicated framework for manufacturing, ISO 23247, that lays out the parts a twin is made of and how they connect. You will probably never read either document — they are written for committees, not plant managers. But the ideas inside them are genuinely useful, because they turn an arm-waving sales pitch into a set of concrete questions you can ask any vendor.",
    },
    {
      kind: "paragraph",
      text:
        "This matters most at two moments: procurement and integration. When you are buying, standards alignment is a fast way to separate a structured platform from a dashboard wearing the label. When you are integrating — connecting the twin to an MES, an ERP, or a customer's quality system — a shared reference model means the two sides are talking about the same things in the same order. Below is what the standards actually say, in plain terms, and what to do with it.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The vocabulary",
      heading: "ISO/IEC 30173 just agrees on the words",
      sub: "Before you can compare two twins, everyone has to mean the same thing by the nouns.",
    },
    {
      kind: "paragraph",
      text:
        "ISO/IEC 30173 is the dictionary. It pins down the terms that get thrown around loosely — what a digital twin is, what synchronization means, and the important distinction between a twin type and a twin instance. A type is the template: 'a grey-iron foundry of this shape'. An instance is your plant, provisioned from that template and fed by your equipment. The standard's own definition is worth holding onto: a digital twin is a fit-for-purpose digital representation of a real thing, kept in sync with that thing. Two phrases there do a lot of work. 'Fit-for-purpose' means fidelity should match the decision, not chase realism for its own sake. 'In sync' means the model is wired to reality, not drawn once and left to drift.",
    },
    {
      kind: "callout",
      tone: "info",
      title: "Type vs. instance, in one line",
      text:
        "A plant template is the twin type; the live plant you run is the twin instance. If a vendor cannot tell you which one you are buying — a reusable model or a one-off drawing of your floor — that is a question worth pressing.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The framework",
      heading: "ISO 23247 says a twin is built from four entities",
      sub: "The manufacturing reference architecture, stripped to its load-bearing idea.",
    },
    {
      kind: "paragraph",
      text:
        "ISO 23247 is the framework specifically for manufacturing, and its centre of gravity is a reference architecture (Part 2) built from four entities. The naming is bureaucratic, but the structure is exactly how a working twin is actually layered. The thing being twinned is the Observable Manufacturing Element. Something has to acquire its data. Something has to model and reason over it. And something has to put it in front of people. Four roles, in order, from the floor to the screen.",
    },
    {
      kind: "processFlow",
      title: "The ISO 23247-2 reference architecture, floor to screen",
      steps: [
        {
          title: "Observable Manufacturing Element",
          detail:
            "The physical thing being twinned — equipment, material, the process itself, the product, the facility.",
          icon: "factory",
        },
        {
          title: "Data Collection & Device Control",
          detail:
            "Acquires readings from the element (and can control it). In practice: gateways, tags and the field protocols beneath them.",
          icon: "plug",
        },
        {
          title: "Core Entity",
          detail:
            // WS-E: this listed "the simulation" among the twin's faculties on a
            // page whose closing section says our components line up with this
            // architecture "by design" — i.e. read as a claim that we simulate.
            // ISO 23247-2 does name simulation as a permitted Core sub-entity,
            // so the standard is described accurately AND our own position is
            // stated, rather than the standard's superset being softened away.
            "The twin's brain — the model, the lineage and the analytics that turn raw readings into meaning. (ISO also allows simulation here; Sahas Forge measures and correlates rather than simulating.)",
          icon: "cpu",
        },
        {
          title: "User Entity",
          detail:
            "The applications and people on top — dashboards, batch views, reports, alarms and the humans making decisions.",
          icon: "lineChart",
        },
      ],
      caption:
        "Four entities, plus synchronization tying the twin back to the element. A tool missing one of these is doing part of the job, not all of it.",
    },
    {
      kind: "paragraph",
      text:
        "The fifth idea, threaded through all four, is synchronization: the loop that keeps the digital representation honest by grounding it in real readings. Without it you have a model that looks like a twin but remembers and reflects nothing. With it, every operation the twin records is anchored to the live data that surrounded it — the difference between a diagram and a twin, in standards language.",
    },
    {
      kind: "callout",
      tone: "info",
      title: "What the OME really covers",
      text:
        "ISO 23247 enumerates eight categories of Observable Manufacturing Element — equipment, material, process, product, facility, personnel, environment, and supporting documents. Most twins model the first five well. Honest vendors will tell you which categories they cover and which they do not.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The connective tissue",
      heading: "Two more parts you will actually feel",
      sub: "The digital thread (Part 5) and twin composition (Part 6) are where the value shows up day to day.",
    },
    {
      kind: "paragraph",
      text:
        "Beyond the architecture, two parts of ISO 23247 describe capabilities you will recognise immediately. Part 5, the digital thread, is the connected chain of identifiers that links every input to every output across the whole process. It is what lets you take a defective casting and walk backward to the exact heat, mould and conditions behind it — or take a suspect heat and walk forward to every part it touched. Part 6, composition, is the principle that a plant-wide twin is assembled from smaller per-asset twins that snap together. You model each furnace, mixer and station once, then compose them into the process. Recognise those? They are traceability and a process graph, named formally.",
    },
    {
      kind: "comparisonBars",
      title: "Illustrative: how completely typical tools cover the framework",
      unit: "/ 4 entities",
      max: 4,
      bars: [
        { label: "A live dashboard / SCADA screen", value: 2, tone: "warning", display: "data + users" },
        { label: "A static 3D / CAD model", value: 1, tone: "danger", display: "model only" },
        { label: "A standards-aligned process twin", value: 4, tone: "success", display: "all four" },
      ],
      caption:
        "Qualitative and illustrative, not a benchmark. The point: many tools cover one or two of the four entities. A twin covers all four — and synchronizes them.",
    },
    {
      kind: "pullQuote",
      quote:
        "Standards do not make a twin good. They make 'is this actually a twin?' a question with a yes-or-no answer instead of a brochure.",
      attribution: "A buyer's rule of thumb",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The honest part",
      heading: "What conformance does — and does not — mean",
    },
    {
      kind: "paragraph",
      text:
        "Here is the caveat the marketing usually skips. There is a real difference between aligning with a standard and being certified to it. Alignment means a platform's architecture, vocabulary and capabilities map cleanly onto the standard's concepts — its entities, its digital thread, its composition model. Certification is a separate, formal, third-party process. A vendor can be genuinely, usefully standards-aligned without holding a certificate, and most credible ones describe themselves exactly that way. Be suspicious of anyone who blurs the two, in either direction: overclaiming certification, or dismissing standards as paperwork.",
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Read the claim carefully",
      text:
        "'Aligns with the ISO 23247 reference architecture, digital thread and composition, using ISO/IEC 30173 vocabulary' is an accurate, checkable statement. 'ISO-certified digital twin' is a much bigger claim — ask to see the certificate, the scope, and which parts it covers.",
    },
    {
      kind: "paragraph",
      text:
        "One practical note for anyone selling into export markets or large OEM supply chains: increasingly, the customer's procurement team is the one asking about standards. Answering 'how does your data model map to ISO 23247?' with specifics — these entities, this thread, these categories covered, these not — is fast becoming part of winning the contract. The standard has quietly become a shared language between you and your customers' auditors.",
    },
    {
      kind: "paragraph",
      text:
        "None of this should change how you run the plant. Your operators will keep thinking in heats, moulds, pour temperatures and rejects — as they should. The standards live underneath that human language, giving it a rigorous skeleton. The right way to use them is not to memorise the parts, but to keep a short checklist in your pocket: does this twin model the real element, collect its data, reason over it, and present it — and can the vendor say honestly where they align and where they do not? If yes, the words finally mean something.",
    },
    {
      kind: "sahasEnables",
      intro:
        "The four-entity architecture, the digital thread and the composition model above are open standards — any serious platform should map to them. What we add is that the mapping is deliberate and documented, so you can answer a procurement questionnaire with specifics rather than hand-waving.",
      capabilities: [
        {
          title: "Built on the four entities",
          detail:
            "The model (Core), gateways and tags (Data Collection), the physical assets (OMEs) and the app (Users) line up with the ISO 23247-2 reference architecture by design.",
          icon: "workflow",
        },
        {
          title: "A genuine digital thread",
          detail:
            "Heat-scoped lineage links every input to every output, so where-from and where-used are queries — exactly the intent of ISO 23247's digital thread.",
          icon: "gitBranch",
        },
        {
          title: "Aligned, and honest about it",
          detail:
            "A maintained mapping states what aligns with the standards and what is designed-for-but-not-yet — alignment claimed, certification never overclaimed.",
          icon: "shieldCheck",
        },
      ],
    },
  ],
};
